import { useQuery } from "@tanstack/react-query";
import { careerPathKeys } from "@/lib/query";
import type { RoleStatus } from "@/schemas/roles";

// Response types mirror `RoleComparison` in src/api/career-path.ts (types are
// co-located with the hook per convention rather than imported across the
// server/client boundary).

export interface ComparisonItem {
  id: string;
  name: string;
  /** Many-to-many as of the category/theme merge — a capability can carry several. */
  categories: { id: string; name: string }[];
}

export interface ComparisonBuckets {
  overlap: ComparisonItem[];
  added: ComparisonItem[];
  removed: ComparisonItem[];
}

export interface ComparisonRole {
  id: string;
  name: string;
  status: RoleStatus;
}

/**
 * A category on either side of the move, with how many capabilities each role
 * draws from it. Category-level "carried over" does not mean the same skill
 * or competency — it means the move stays within the same kind of capability,
 * which is the point (originally WI-321's competency-only "theme"; extended
 * to skills once categories and themes merged).
 */
export interface CategoryComparisonItem {
  id: string;
  name: string;
  fromCount: number;
  toCount: number;
}

export interface CategoryComparisonBuckets {
  overlap: CategoryComparisonItem[];
  added: CategoryComparisonItem[];
  removed: CategoryComparisonItem[];
}

/** How much of each side is filed under a category at all. */
export interface CategoryCoverage {
  fromCapabilities: number;
  fromCategorized: number;
  toCapabilities: number;
  toCategorized: number;
}

export interface RoleComparison {
  from: ComparisonRole;
  to: ComparisonRole;
  skills: ComparisonBuckets;
  competencies: ComparisonBuckets;
  categories: CategoryComparisonBuckets;
  categoryCoverage: CategoryCoverage;
}

/** Sentinel so consumers can tell "a role vanished" from a network failure. */
export const ROLE_NOT_FOUND = "ROLE_NOT_FOUND";

export function useRoleComparison(fromId: string | null, toId: string | null) {
  return useQuery({
    queryKey: careerPathKeys.compare(fromId ?? "", toId ?? ""),
    // Same role on both sides is allowed — that's the "compare two levels of
    // one role" case, where the requirement sets are identical by definition
    // and the per-level expectations are the whole point.
    enabled: Boolean(fromId && toId),
    queryFn: async (): Promise<RoleComparison> => {
      const params = new URLSearchParams({ from: fromId ?? "", to: toId ?? "" });
      const res = await fetch(`/api/roles/compare?${params}`);
      if (res.status === 404) throw new Error(ROLE_NOT_FOUND);
      if (!res.ok) throw new Error("Failed to compare roles");
      const json = await res.json();
      return json.data;
    },
  });
}
